import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector } from 'react-redux';
import { Close, Create } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import { useQuery } from '@tanstack/react-query';

import BDHelmet from '@/components/BDHelmet';
import BaseInputValidation from '@/components/ui/Input/BaseInputValidation';
import BaseSelect from '@/components/ui/Select/BaseSelect';
import ChangePassword from '@/components/Modal/Auth/ChangePassword';
import Loader from '@/components/ui/Loader/Loader';

import usePatch from '@/hooks/usePatch';
import user from '@/api/user';
import userIcon from '@/assets/icons/user-xl.png';

const schema = yup.object().shape({
	username: yup.string().required(),
	email: yup.string().required('Email is a required field').email('Email not valid'),
	phonenumber: yup.string().required(),
	privilegelevelid: yup.string().required(),
});

const { getUser } = user();

export default function User() {
	const user = useSelector((state) => state.auth.user);
	const privilegeLevels = useSelector((state) => state.content.privilegeLevels);

	const [update, setUpdate] = useState(false);
	const [file, setFile] = useState(null);
	const [updatePassword, setUpdatePassword] = useState(null);

	const {
		data: p,
		isFetching: isFetchingP,
		isFetched: isFetchedP,
		refetch,
	} = useQuery({
		queryKey: ['user', user],
		enabled: !!user,
		queryFn: () => getUser(user.userid),
	});
	const data = useMemo(() => p?.data || null, [p]) || null;

	const {
		setValue,
		handleSubmit,
		control,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(schema),
		defaultValues: {},
		reValidateMode: 'onChange',
	});

	useEffect(() => {
		if (data) {
			setValue('username', data.username);
			setValue('email', data.email);
			setValue('department', data.departement);
			setValue('phonenumber', data.phonenumber);
			setValue('location', data.location);
			setValue('privilegelevelid', data.privilegelevelid);
		}
	}, [data, setValue]);

	const { getRootProps, getInputProps } = useDropzone({
		accept: {
			'image/*': '*',
		},
		multiple: false,
		onDrop: (file) => {
			setFile(
				Object.assign(file, {
					preview: URL.createObjectURL(file),
				})
			);
		},
	});

	const { mutate: updateUser } = usePatch({
		url: `usertabledoc/${user.userid}`,
		method: 'patch',
		callback: async (res) => {
			if (res.status === 200 && res.data) {
				refetch();
				toast.success(res.data?.message || 'User updated successfully');
			}
		},

	});

	const onSubmit = async (formData) => {
		const { username, email, phonenumber, departement, location, privilegelevelid } = formData
		const fData = {
			// ...data,
			username,
			email,
			phonenumber,
			departement,
			location,
			privilegelevelid,
		};

		updateUser(fData);
	};

	return (
		<>
			<BDHelmet title='User' description='BDS Samferdsel user' />
			<div className='container flex flex-col gap-4 pt-8 pb-8 mx-auto'>
				<h1 className='section-title'>Brukerprofil</h1>
				<div className='flex justify-between'>
					<span>Brukerdetaljer</span>
					<IconButton onClick={() => setUpdate(!update)} aria-label='update' className='!bg-white !p-0 !px-1 !text-base !rounded-sm'>
						{update ? <Close fontSize='inherit' /> : <Create fontSize='inherit' />}
					</IconButton>
				</div>
				<hr />
				<form onSubmit={handleSubmit(onSubmit)} className='flex w-full gap-4'>
					{isFetchingP && !isFetchedP && <Loader />}

					<div className='flex flex-col gap-2 min-w-48 min-h-48'>
						<div className='w-full h-full p-4 bg-white'>
							{user.avatar ? (
								<img src={user.avatar} className='object-contain w-full h-full' />
							) : (
								<div className='bg-[#D1D1D1] w-full h-full rounded-full'>
									<img src={userIcon} className='object-contain w-full' />
								</div>
							)}
						</div>
						{update && (
							<div {...getRootProps({ className: 'dropzone w-fit flex flex-col gap-2 cursor-pointer' })}>
								<div className='flex items-center gap-4'>
									<label className='flex items-center'>
										<input {...getInputProps()} />
										<button className='h-10 px-4 text-white border border-gray-700 rounded-sm bg-bd-blue' type='button'>
											<span>Velg filer</span>
										</button>
									</label>
									{!file && <span>Ingen fil valgt</span>}
								</div>
							</div>
						)}
					</div>
					<div className='grid w-full grid-cols-10 gap-2'>
						<div className='flex flex-col col-span-5 gap-4'>
							<BaseInputValidation
								control={control}
								name='username'
								label='Brukernavn'
								errors={errors}
								required
								config={{ disabled: !update }}
							/>
							<BaseInputValidation control={control} name='email' label='E-post' errors={errors} required config={{ disabled: !update }} />
							<BaseInputValidation
								control={control}
								name='departement'
								label='Avdeling'
								errors={errors}
								config={{ disabled: !update }}
							/>
							<BaseInputValidation control={control} name='password' label='Passord' errors={errors} required config={{ disabled: true }} />
						</div>
						<div className='flex flex-col items-start col-span-5 gap-4'>
							<BaseInputValidation
								control={control}
								name='phonenumber'
								label='Telefonnummer'
								errors={errors}
								required
								config={{ disabled: !update }}
							/>
							<BaseInputValidation
								control={control}
								name='location'
								label='Plassering'
								errors={errors}
								required
								config={{ disabled: !update }}
							/>
							<BaseSelect
								control={control}
								name='privilegelevelid'
								label='Type'
								errors
								data={privilegeLevels?.filter((p) => ![99, 1].includes(p.privilegelevelid))}
								config={{
									styles: {},
									classes: '',
									disabled: !update,
									optionLabel: '--Select Version--',
									keyVal: 'privilegelevelid',
									keyName: 'privilegelevelname',
									keyLabel: 'privilegelevelname',
								}}
								size='small'
							/>
							<button type='button' className='text-bd-blue' onClick={() => setUpdatePassword(true)}>
								Endre passord
							</button>
							<button className='ml-auto btn' disabled={!update}>
								Oppdater
							</button>
						</div>
					</div>
				</form>

				{updatePassword && <ChangePassword open={!!updatePassword} onClose={() => setUpdatePassword(false)} />}
			</div>
		</>
	);
}
